import { useNavigation } from '@react-navigation/native';
import { BaseText, Meta, theme } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import WarningWrapper from 'components/park/ParkingZoneWarningWrapper';
import ParkingContext from 'context/ParkingContext';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ViewProps } from 'react-native';
import styled from 'styled-components/native';
import { Divider } from 'styles/CommonLayoutStyles';
import { SimpleFullContainer, StyledWrapper } from 'styles/ContainerStyles';
import { CountryCode, Maybe, ParkZoneNotes } from 'types/generatedSchemaTypes';
import { ParkNavProps } from 'types/navigation';
import { countryCodeToLang, getLang } from 'utils/commonUtils';
import { isValidNote } from 'utils/parkUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { Row } from './styles/styles';

const WeightedMeta = styled(Meta)`
  font-weight: 500;
`;
export type ImportantNotesProps = {
  fromCarousel?: boolean;
};

export const ImportantZoneNotes: React.FC<ViewProps & ImportantNotesProps> = ({
  fromCarousel,
}) => {
  const { parkingZoneResult } = useContext(ParkingContext);
  const parkZoneNotes = parkingZoneResult?.data?.parkZone?.notes;
  const country = parkingZoneResult?.data?.parkZone?.countryCode;

  const importantNotes = useMemo(
    () => parkZoneNotes?.filter((note) => isValidNote(note, country, 3)),
    [country, parkZoneNotes]
  );
  const { navigate } = useNavigation<ParkNavProps>();
  return (
    <>
      {importantNotes && importantNotes.length > 0 && (
        <Pressable
          style={({ pressed }: any) => [
            { opacity: pressed && fromCarousel ? 0.7 : 1 },
          ]}
          onPress={() => {
            if (fromCarousel) {
              navigate('parkInfo', {
                parkingZoneUid: parkingZoneResult?.data?.parkZone
                  ?.uid as string,
              });
            }
          }}>
          <WarningWrapper isWarning>
            <Icon name="Info-Outlined" />
            <ParkingZoneNotes
              testID="park-zone-important-information"
              parkZoneNotes={importantNotes}
              important
              maxLines={fromCarousel ? 2 : undefined}
              country={country}
            />
          </WarningWrapper>
        </Pressable>
      )}
    </>
  );
};

export const OtherNotes: React.FC = () => {
  const { parkingZoneResult } = useContext(ParkingContext);
  const parkZoneNotes = parkingZoneResult?.data?.parkZone?.notes;
  const country = parkingZoneResult?.data?.parkZone?.countryCode;
  const otherNotes = useMemo(
    () => parkZoneNotes?.filter((note) => isValidNote(note, country, 1, 2)),
    [country, parkZoneNotes]
  );

  return (
    <>
      {otherNotes && otherNotes.length > 0 && (
        <WarningWrapper marginTop marginBottom>
          <Icon name="Info-Outlined" />
          <ParkingZoneNotes
            testID="park-zone-other-information"
            parkZoneNotes={otherNotes}
            country={country}
          />
        </WarningWrapper>
      )}
    </>
  );
};

const ParkingZoneNotes = ({
  testID,
  headline,
  parkZoneNotes,
  maxLines,
  country,
  important,
}: {
  testID: string;
  headline?: string;
  parkZoneNotes: ParkZoneNotes[];
  important?: boolean;
  maxLines?: number;
  country?: Maybe<CountryCode>;
}) => {
  const { t } = useTranslation();

  return (
    <SimpleFullContainer>
      {headline && (
        <Row mb={10}>
          <BaseText testID={`${testID}-headline`} numberOfLines={1}>
            {t(headline)}
          </BaseText>
        </Row>
      )}
      {parkZoneNotes.map(({ text }, i, { length }) => {
        const currentLang = getLang();
        const countryLang = countryCodeToLang(country as CountryCode);
        const noteText = text[currentLang] ?? text.en ?? text[countryLang];
        return (
          <StyledWrapper mb={5} key={`${testID}-${i}`}>
            <WeightedMeta
              numberOfLines={maxLines}
              color={theme.colors.black}
              {...testIdentifiers(`${testID}-meta-${i}`)}>
              {noteText}
            </WeightedMeta>
            {length - 1 !== i && (
              <Row mb={5}>
                <Divider
                  marginVertical={0}
                  color={
                    important
                      ? theme.colors.black
                      : theme.colors.separatorOnGreen
                  }
                />
              </Row>
            )}
          </StyledWrapper>
        );
      })}
    </SimpleFullContainer>
  );
};

export default ParkingZoneNotes;
